import { useEffect, useRef, Fragment } from "react";
import AdBlock from "@/components/AdBlock";
import { Button } from "@/components/ui/button";
import { Phone, Video, Shirt, Send, ImagePlus, Gift, CheckCheck, RotateCw, Menu } from "lucide-react";

function TypingDots() {
  return (
    <div className="flex items-center gap-1 px-4 py-3">
      {[0, 1, 2].map((i) => (
        <span key={i} className="w-2 h-2 rounded-full bg-muted-foreground animate-typing" style={{ animationDelay: `${i * 160}ms` }} />
      ))}
    </div>
  );
}

function Avatar({ character, size = "w-11 h-11" }) {
  const img = character.active_outfit_url || character.avatar_url;
  if (img) return <img src={img} alt={character.name} className={`${size} rounded-full object-cover shrink-0`} />;
  return (
    <div className={`${size} rounded-full bg-gradient-to-br from-brand to-cyan-500 flex items-center justify-center font-heading font-bold text-white shrink-0`}>
      {(character.name || "?")[0].toUpperCase()}
    </div>
  );
}

const iconBtn = "w-11 h-11 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors shrink-0";
const dockBtn = "w-11 h-11 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center shrink-0 transition-colors";

export default function ChatPane({
  character, messages, sending, draft, setDraft, onSend, onRetry,
  onVoice, onVideo, onSelfie, onWardrobe, onGift, onMenu, variant = "hub",
}) {
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView(); }, [messages.length, sending]);

  const menuBtn = onMenu && (
    <button data-testid="mobile-menu-button" aria-label="Open menu" onClick={onMenu} className={`lg:hidden ${iconBtn}`}>
      <Menu className="w-5 h-5" />
    </button>
  );

  if (!character) {
    return (
      <div className="h-full flex flex-col">
        {menuBtn && <div className="lg:hidden p-3 border-b border-white/10">{menuBtn}</div>}
        <div className="flex-1 flex items-center justify-center text-muted-foreground p-6 text-center">Select or create a companion to start.</div>
      </div>
    );
  }
  const isHub = variant === "hub";

  return (
    <div className="h-full flex flex-col bg-ink relative overflow-hidden" data-testid="chat-pane">
      {/* header */}
      <div className="order-1 bg-surface border-b border-white/10 px-3 sm:px-6 py-2.5 sm:py-3 flex items-center gap-2.5 sm:gap-3 z-20">
        {menuBtn}
        <Avatar character={character} size="w-10 h-10 sm:w-11 sm:h-11" />
        <div className="min-w-0 flex-1">
          <p className="font-heading font-semibold leading-tight truncate">{character.name}</p>
          <p className="text-xs text-brand flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" /> online
          </p>
        </div>
        {isHub && (
          <>
            <button data-testid="voice-call-button" aria-label="Voice call" onClick={onVoice} className={iconBtn}><Phone className="w-4 h-4" /></button>
            <button data-testid="video-call-button" aria-label="Video call" onClick={onVideo} className={iconBtn}><Video className="w-4 h-4" /></button>
            <button data-testid="change-outfit-button" aria-label="Change outfit" onClick={onWardrobe} className={iconBtn}><Shirt className="w-4 h-4" /></button>
          </>
        )}
      </div>

      {/* messages */}
      <div className="order-2 flex-1 overflow-y-auto overscroll-contain px-3 sm:px-6 py-4 sm:py-5 space-y-3 relative z-10">
        {isHub && <AdBlock id="ad-slot-chat-top" size="468x60" desktopOnly className="w-full max-w-[468px] h-[60px] mx-auto mb-2" />}
        {messages.length === 0 && (
          <div className="text-center py-10">
            <div className="inline-block"><Avatar character={character} size="w-20 h-20" /></div>
            <p className="text-muted-foreground text-sm mt-4">Say hello to {character.name} 💬</p>
          </div>
        )}
        {messages.map((m, i) => {
          const mine = m.sender === "user";
          const showAd = isHub && (i + 1) % 10 === 0;
          return (
            <Fragment key={m.id}>
              <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div data-testid={`message-${m.sender}`}
                  className={`max-w-[85%] sm:max-w-[78%] px-4 py-2.5 leading-relaxed text-[15px] ${mine
                    ? "bg-brand text-white rounded-t-2xl rounded-l-2xl rounded-br-sm"
                    : "bg-surface border border-white/10 rounded-t-2xl rounded-r-2xl rounded-bl-sm"}`}>
                  {m.image
                    ? <img src={m.image} alt="selfie" className="rounded-xl max-w-full w-56" />
                    : <p className="whitespace-pre-wrap break-words">{m.content}</p>}
                  {mine && !m.failed && <span className="flex items-center justify-end gap-0.5 mt-1 opacity-70"><CheckCheck className="w-3.5 h-3.5" /></span>}
                </div>
              </div>
              {m.failed && (
                <div className="flex justify-end">
                  <button type="button" data-testid="retry-message-button" disabled={sending} onClick={() => onRetry?.(m)}
                    className="flex items-center gap-1 text-xs text-red-400 hover:text-red-300 disabled:opacity-40 py-1.5">
                    <RotateCw className="w-3 h-3" /> Not sent · Tap to retry
                  </button>
                </div>
              )}
              {showAd && <AdBlock id={i === 9 ? "ad-slot-chat-stream" : `ad-slot-chat-stream-${i}`} size="native" label="Native Banner" className="h-24 my-1" />}
            </Fragment>
          );
        })}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-surface border border-white/10 rounded-t-2xl rounded-r-2xl rounded-bl-sm"><TypingDots /></div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* bottom ad: above the input on phones, below it on desktop */}
      {isHub && (
        <div className="order-3 md:order-5">
          <AdBlock id="ad-slot-bottom-rail" size="728x90" mobileSize="320x50" className="h-[50px] md:h-[90px] rounded-none border-x-0 border-b-0" />
        </div>
      )}

      {/* input dock */}
      <div className="order-4 bg-surface border-t border-white/10 px-2.5 pt-2.5 sm:p-4 pb-[max(0.625rem,env(safe-area-inset-bottom))] z-20">
        <form onSubmit={(e) => { e.preventDefault(); onSend(); }} className="flex items-center gap-2 w-full">
          {isHub && (
            <>
              <button type="button" data-testid="gift-button" aria-label="Send gift" onClick={onGift} className={dockBtn}><Gift className="w-5 h-5 text-brand" /></button>
              <button type="button" data-testid="selfie-request-button" aria-label="Request selfie" onClick={onSelfie} className={dockBtn}><ImagePlus className="w-5 h-5 text-brand" /></button>
            </>
          )}
          <input data-testid="chat-input" value={draft} onChange={(e) => setDraft(e.target.value)}
            onFocus={() => setTimeout(() => endRef.current?.scrollIntoView({ block: "end" }), 300)}
            enterKeyHint="send" autoComplete="off" placeholder={`Message ${character.name}…`}
            className="flex-1 min-w-0 h-11 bg-ink border border-white/10 rounded-full px-4 text-base outline-none focus:border-brand/50 transition-colors" />
          <Button type="submit" data-testid="send-button" aria-label="Send" disabled={!draft.trim() || sending}
            className="w-11 h-11 rounded-full bg-brand hover:bg-brand-hover text-white p-0 shrink-0 transition-colors disabled:opacity-40"><Send className="w-5 h-5" /></Button>
        </form>
      </div>
    </div>
  );
}
